/**
 * Deterministic skill overlap fitment (MAT-020 / APP-060) — complements AI fitment in Single Optimize.
 * Use for bulk import and quick recruiter triage without calling LLM.
 */

function normSkill(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function tokenizeSkills(raw: string[]): string[] {
  const out = new Set<string>();
  for (const r of raw) {
    for (const part of r.split(/[,;/|]/)) {
      const n = normSkill(part);
      if (n.length > 0) out.add(n);
    }
  }
  return [...out];
}

export interface SkillFitmentResult {
  fitment_score: number;
  fitment_json: Record<string, unknown>;
}

/**
 * Coverage of **required** job skills that appear on the candidate profile (case-insensitive).
 * Score 0–100; JSON includes gaps for UI and audit.
 */
export function computeSkillFitment(jobRequired: string[], candidateSkills: string[]): SkillFitmentResult {
  const req = tokenizeSkills(jobRequired);
  const cand = new Set(tokenizeSkills(candidateSkills));

  if (req.length === 0) {
    return {
      fitment_score: 100,
      fitment_json: {
        method: 'skill_overlap_v1',
        note: 'No required skills on job — default score 100.',
        matched_skills: [],
        missing_skills: [],
        extra_skills: [...cand],
      },
    };
  }

  const matched: string[] = [];
  const missing: string[] = [];
  for (const s of req) {
    if (cand.has(s)) matched.push(s);
    else missing.push(s);
  }

  const ratio = matched.length / req.length;
  const fitment_score = Math.round(Math.min(100, Math.max(0, ratio * 100)) * 100) / 100;

  const extra = [...cand].filter((c) => !req.includes(c));

  return {
    fitment_score,
    fitment_json: {
      method: 'skill_overlap_v1',
      required_count: req.length,
      matched_count: matched.length,
      coverage_ratio: Math.round(ratio * 1000) / 1000,
      matched_skills: matched,
      missing_skills: missing,
      extra_skills: extra,
      skill_gap_summary:
        missing.length === 0
          ? 'All listed required skills appear on the candidate profile.'
          : `Missing vs JD: ${missing.slice(0, 8).join(', ')}${missing.length > 8 ? '…' : ''}`,
    },
  };
}
